import { useEffect, useState } from "react";
import config from "@/config";

/**
 * EnvIndicator — two intentionally cryptic dots in the bottom-left corner.
 *
 *   ● ●
 *   │ └─ DB  : which database the backend is actually connected to
 *   └─── API : which backend this UI build points at (baked from VITE_API_URL)
 *
 * Color code (internal only — no text labels, so clients can't decode it):
 *   🟠 amber = dev     🟢 green = prod     ⚪ grey = local / unknown
 *
 * Examples:
 *   green + green  → prod API + prod DB   (fully prod)
 *   green + amber  → prod API + dev  DB   (mismatch! API is prod but DB is dev)
 *   amber + amber  → dev API + dev DB     (fully dev)
 *
 * The API dot comes from the baked API URL; the DB dot comes from the API's
 * unauthenticated /System/Info endpoint (labels only, no secrets). Both are
 * also logged to the console on load for your own verification.
 */

const API_URL = config.api?.API_URL ?? "";

function colorFor(label: string): string {
  switch (label) {
    case "prod":
      return "#2bb673"; // green
    case "dev":
      return "#f5a623"; // amber
    case "loading":
      return "#c8ccd1"; // light grey (pending)
    default:
      return "#9aa0a6"; // grey (local / unknown)
  }
}

function resolveApiEnv(url: string): string {
  if (/-prod\b/i.test(url) || /-prod\./i.test(url)) return "prod";
  if (/-dev\b/i.test(url) || /-dev\./i.test(url)) return "dev";
  if (/localhost|127\.0\.0\.1/i.test(url)) return "local";
  return "unknown";
}

const API_ENV = resolveApiEnv(API_URL);

// One-time console breadcrumb for the API target.
// eslint-disable-next-line no-console
console.info(
  `%c● API ${API_ENV.toUpperCase()}`,
  `color:${colorFor(API_ENV)};font-weight:700`,
  `→ ${API_URL}`
);

function Dot({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
        boxShadow: `0 0 0 1px rgba(0,0,0,0.15), 0 0 6px ${color}`,
        opacity: 0.85,
      }}
    />
  );
}

export function EnvIndicator() {
  const [dbEnv, setDbEnv] = useState<string>("loading");

  useEffect(() => {
    let active = true;

    fetch(`${API_URL}/System/Info`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!active) return;
        const db = String(data?.database ?? "unknown").toLowerCase();
        setDbEnv(db);
        // eslint-disable-next-line no-console
        console.info(
          `%c● DB ${db.toUpperCase()}`,
          `color:${colorFor(db)};font-weight:700`,
          `(api=${data?.apiEnvironment ?? "?"}, storage=${data?.fileStorage ?? "?"})`
        );
      })
      .catch(() => {
        if (active) setDbEnv("unknown");
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        left: 8,
        bottom: 8,
        display: "flex",
        gap: 4,
        zIndex: 2147483647,
        pointerEvents: "none",
      }}
    >
      <Dot color={colorFor(API_ENV)} />
      <Dot color={colorFor(dbEnv)} />
    </div>
  );
}

export default EnvIndicator;
